import {
  reportGroupType,
  reportNavType,
} from '@edulastic/constants/const/report'

/**
 * Reverses share-time filter transformations (HLD §4.5) so a resolved share
 * becomes the URL query each report's filter component parses on first load.
 * Keys absent from the result fall back to the viewer's normal defaults.
 */

// Never forwarded: `reportId` switches on the read-only shared view, and the
// DW term expansion is recalculated for the viewer.
const ALWAYS_DROPPED_KEYS = [
  'reportId',
  'termIds',
  'isLongitudinalReport',
  'districtGroupIds',
]

export const DD_FILTER_PARAM = 'ddfilter'

const toCsv = (value) => (Array.isArray(value) ? value.join(',') : value)

const omitKeys = (filters, keys) => {
  const result = { ...filters }
  keys.forEach((key) => delete result[key])
  return result
}

const renameKey = (filters, from, to) => {
  if (filters[from] === undefined) {
    return filters
  }
  const { [from]: value, ...rest } = filters
  return rest[to] === undefined ? { ...rest, [to]: value } : rest
}

/** Demographics travel as one JSON param; MAR/SMR seed tempDdFilter from it. */
const withDdFilterParam = (filters) => {
  const { ddfilter, ...rest } = filters
  const values = Object.entries(ddfilter || {}).filter(
    ([, value]) => value !== '' && value !== undefined && value !== null
  )
  return values.length
    ? { ...rest, [DD_FILTER_PARAM]: JSON.stringify(Object.fromEntries(values)) }
    : rest
}

const ADAPTERS = {
  [reportGroupType.SINGLE_ASSESSMENT_REPORT]: (filters) =>
    // `testId` goes in the path; the filter reads the band as performanceBandProfile
    renameKey(
      omitKeys(filters, ['testId']),
      'profileId',
      'performanceBandProfile'
    ),

  [reportGroupType.MULTIPLE_ASSESSMENT_REPORT]: (filters, reportType) => {
    let result = renameKey(filters, 'compareBy', 'selectedCompareBy')
    if (
      reportType === reportNavType.PRE_VS_POST &&
      !result.preTestId &&
      !result.postTestId
    ) {
      const testIds = `${toCsv(result.testIds) || ''}`
        .split(',')
        .filter(Boolean)
      if (testIds.length === 2) {
        ;[result.preTestId, result.postTestId] = testIds
        result = omitKeys(result, ['testIds'])
      }
    }
    return withDdFilterParam(result)
  },

  [reportGroupType.STANDARDS_MASTERY_REPORT]: (filters) =>
    withDdFilterParam(filters),

  [reportGroupType.STUDENT_PROFILE_REPORT]: (filters) =>
    renameKey(
      renameKey(omitKeys(filters, ['studentId']), 'domainIds', 'domainId'),
      'standardIds',
      'standardId'
    ),

  [reportGroupType.MULTIPLE_ASSESSMENT_REPORT_DW]: (filters) => filters,

  [reportGroupType.WHOLE_LEARNER_REPORT]: (filters) => {
    let result = omitKeys(filters, ['studentId'])
    result = renameKey(result, 'districtIds', 'districtId')
    result = renameKey(result, 'assessmentTypes', 'testTypes')
    result = renameKey(result, 'domainIds', 'domainId')
    return renameKey(result, 'standardIds', 'standardId')
  },

  [reportGroupType.DW_EFFICACY_REPORT]: (filters) =>
    renameKey(filters, 'compareBy', 'selectedCompareBy'),

  [reportGroupType.DW_DOMAIN_ANALYSIS_REPORT]: (filters) => filters,
}

export const fromSharedFilters = (family, reportType, filters) => {
  const adapt = ADAPTERS[family] || ((f) => f)
  const adapted = adapt(omitKeys(filters, ALWAYS_DROPPED_KEYS), reportType)
  return Object.fromEntries(
    Object.entries(adapted)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, toCsv(value)])
  )
}

/** Parses the `ddfilter` URL param written by `fromSharedFilters`. */
export const parseDdFilterParam = (value) => {
  if (!value) {
    return null
  }
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : null
  } catch (e) {
    return null
  }
}

/** Inverse of `parseDdFilterParam`: undefined when no demographic is set. */
export const buildDdFilterParam = (ddfilter) => {
  const values = Object.entries(ddfilter || {}).filter(
    ([, value]) => value !== '' && value !== 'all' && value != null
  )
  return values.length ? JSON.stringify(Object.fromEntries(values)) : undefined
}
